import { useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { tokens } from '../../global/theme/tokens';
import { isBusyStatus } from './useAiChatSession';
import { AiChatOptionsMenu } from './AiChatOptionsMenu';

export const AiChatHeader = ({
  chats,
  chatsLoading,
  chat,
  currentUserId,
  scopeLabel,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  budget,
  permissions,
  canEditPermissions,
  reasoningEffort,
  onReasoningEffortChange,
  canChangeEffort,
  fullscreen,
  onToggleFullscreen,
  onClose,
  isMobile,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const closeMenu = () => {
    setMenuAnchor(null);
    setConfirmDeleteId(null);
  };

  const handleDelete = (e, chatId) => {
    e.stopPropagation();
    if (confirmDeleteId !== chatId) return setConfirmDeleteId(chatId);
    setConfirmDeleteId(null);
    onDeleteChat(chatId).catch(() => {});
  };

  return (
    <Box className="flex items-center gap-2 px-3 pt-3 pb-2">
      <Box
        className="flex items-center justify-center rounded-xl flex-shrink-0"
        sx={{
          width: 34,
          height: 34,
          backgroundColor: `${colors.info}20`,
          color: colors.info,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20 }} />
      </Box>

      <Box
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        className="flex flex-col min-w-0 flex-1 cursor-pointer rounded-lg px-1.5 py-0.5"
        sx={{ '&:hover': { backgroundColor: colors.bgInput } }}
      >
        <Box className="flex items-center gap-0.5 min-w-0">
          <Typography variant="h6" fontWeight={600} noWrap>
            {chat?.title || t('New AI chat')}
          </Typography>
          {chatsLoading ? (
            <CircularProgress size={12} sx={{ ml: 0.5 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.6 }} />
          )}
        </Box>
        <Box className="flex items-center gap-1 min-w-0" sx={{ opacity: 0.6 }}>
          {scopeLabel && <AccountTreeIcon sx={{ fontSize: 12 }} />}
          <Typography variant="caption" noWrap>
            {scopeLabel || t('General chats')}
          </Typography>
        </Box>
      </Box>

      <AiChatOptionsMenu
        budget={budget}
        permissions={permissions}
        canEditPermissions={canEditPermissions}
        reasoningEffort={reasoningEffort}
        onReasoningEffortChange={onReasoningEffortChange}
        canChangeEffort={canChangeEffort}
      />
      <Tooltip title={t('New AI chat')}>
        <IconButton size="small" onClick={onNewChat}>
          <EditNoteIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      {!isMobile && (
        <Tooltip title={fullscreen ? t('Exit fullscreen') : t('Fullscreen')}>
          <IconButton size="small" onClick={onToggleFullscreen}>
            {fullscreen ? (
              <CloseFullscreenIcon sx={{ fontSize: 18 }} />
            ) : (
              <OpenInFullIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('Close')}>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            width: 320,
            maxHeight: 420,
          },
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ px: 2, pt: 0.5, pb: 1, opacity: 0.6 }}
        >
          {scopeLabel
            ? t('Chats in {{project}}', { project: scopeLabel })
            : t('General chats')}
        </Typography>
        {chats.length === 0 && (
          <Typography variant="body2" sx={{ px: 2, py: 1, opacity: 0.5 }}>
            {t('No chats yet')}
          </Typography>
        )}
        {chats.map((item) => {
          const isCreator = String(item.createdBy) === String(currentUserId);
          const confirming = confirmDeleteId === item.id;
          return (
            <MenuItem
              key={item.id}
              selected={item.id === chat?.id}
              onClick={() => {
                onSelectChat(item.id);
                closeMenu();
              }}
              sx={{
                mx: 1,
                borderRadius: '8px',
                gap: 1,
                '& .ai-chat-delete': { opacity: confirming ? 1 : 0 },
                '&:hover .ai-chat-delete': { opacity: 1 },
              }}
            >
              <ListItemText
                primary={item.title}
                secondary={moment(item.lastMessageAt || item.createdAt).fromNow()}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { opacity: 0.6 },
                }}
              />
              {isBusyStatus(item.status) && (
                <CircularProgress size={12} sx={{ color: colors.info }} />
              )}
              {isCreator && (
                <Tooltip title={confirming ? t('Click again to delete') : t('Delete')}>
                  <IconButton
                    size="small"
                    className="ai-chat-delete"
                    onClick={(e) => handleDelete(e, item.id)}
                    sx={{ color: confirming ? colors.error : colors.text }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
